import { groupRequest } from './publishingGroups';
import config from './publishingGroupConfig.json';

async function upload(file, owner) {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) throw new Error('Choose a PNG, JPEG, or WebP image up to 2 MB.');
  const data = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('Unable to read image.'));
    reader.readAsDataURL(file);
  });
  const result = await groupRequest('/welcome-image', { method: 'POST', ...(owner ? {owner} : {}), body: { contentType: file.type, data } });
  let url; try { url = new URL(result.url); } catch { throw new Error('Image upload could not be confirmed.'); }
  if (url.protocol !== 'https:' || !url.hostname.endsWith('.s3.us-west-2.amazonaws.com') || url.port || url.username || url.password || url.search || url.hash || (owner && !url.pathname.startsWith(`/public/${owner}/`)) || !/\.(png|jpg|webp)$/.test(url.pathname)) throw new Error('Image upload could not be confirmed.');
  return url.href;
}
const api = kind => ({
  list: async () => (await groupRequest(`/content?kind=${kind}`)).items,
  get: async id => (await groupRequest(`/content?kind=${kind}&id=${encodeURIComponent(id || 'settings')}`)).item || {},
  save: item => groupRequest(`/content?kind=${kind}`, { method: 'PUT', body: item }),
  remove: id => groupRequest(`/content?kind=${kind}&id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
  upload,
});
export function createGroupBlogApi(owner) {
  const request = (path, options) => {
    if (!owner) throw new Error('Reload the group portal before editing posts.');
    return groupRequest(path, {...options, owner});
  };
  return {
    versioned: true,
    list: async signal => {
      const data = await request('/content?kind=posts',{signal});
      if (!Array.isArray(data.items)) throw new Error('Your posts could not be loaded completely. Please retry.');
      return data.items.map(post => ({...post,version:post.version ?? ''}));
    },
    save: post => request('/content?kind=posts',{method:'PUT',body:post}),
    remove: post => request(`/content?kind=posts&id=${encodeURIComponent(post.id)}&version=${encodeURIComponent(post.version)}`,{method:'DELETE'}),
    upload: file => {if(!owner)throw new Error('Reload the group portal before uploading.');return upload(file,owner);},
  };
}
export function createGroupPagesApi(owner) {
  const request = (path, options) => {
    if (!owner) throw new Error('Reload the group portal before editing pages.');
    return groupRequest(path, {...options, owner});
  };
  return {
    versioned: true,
    list: async () => (await request('/content?kind=pages')).items,
    get: async slug => (await request(`/content?kind=pages&id=${encodeURIComponent(slug)}`)).item,
    save: page => request('/content?kind=pages', {method:'PUT',body:page}),
    remove: (slug, version) => request(`/content?kind=pages&id=${encodeURIComponent(slug)}&version=${encodeURIComponent(version)}`, {method:'DELETE'}),
    upload: file => {if(!owner)throw new Error('Reload the group portal before uploading.');return upload(file,owner);},
  };
}
export const groupNewsletterApi = api('newsletter');
export function createGroupNewsletterApi(owner) {
  function read(item, requiredVersion = false) {
    if (item === null && !requiredVersion) return { newsletterTitle: '', newsletterTagline: '', version: '' };
    if (!item || typeof item.newsletterTitle !== 'string' || typeof item.newsletterTagline !== 'string' ||
        (item.version !== undefined && typeof item.version !== 'string') || (requiredVersion && !item.version)) throw new Error('Saved newsletter settings could not be confirmed. Reload the saved page.');
    return { ...item, version: item.version || '' };
  }
  const request = async (path, options) => {
    if (!owner) throw new Error('Reload your group portal before editing newsletter settings.');
    return groupRequest(path, { ...options, owner });
  };
  return {
    versioned: true,
    get: async () => read((await request('/content?kind=newsletter&id=settings')).item),
    save: async value => read((await request('/content?kind=newsletter', { method: 'PUT', body: value })).item, true),
  };
}
export async function subscribeToGroup(body, {signal} = {}) {
  const controller = new AbortController(); let timer, rejectCanceled;
  const canceled = new Promise((_, reject) => { rejectCanceled = reject; });
  const abort = () => { controller.abort(); rejectCanceled(new Error('Signup was interrupted. Please try again.')); };
  signal?.addEventListener('abort', abort);
  if (signal?.aborted) abort();
  const work = async () => {
    if (controller.signal.aborted) throw new Error('Signup was interrupted. Please try again.');
    const response = await fetch(`${config.apiBase}/subscribe?host=${encodeURIComponent(window.location.hostname)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data?.error === 'string' ? data.error : 'Unable to subscribe. Please try again.');
    if (data?.subscribed !== true) throw new Error('Signup could not be confirmed. Please try again.');
    return { subscribed: true, message: 'Thank you! Your newsletter signup request has been received.' };
  };
  try { return await Promise.race([work(), canceled, new Promise((_,reject) => { timer=setTimeout(()=>{controller.abort();reject(new Error('Signup took too long. Please try again.'));},20000); })]); }
  catch(error) { if (error instanceof SyntaxError || error instanceof TypeError) throw new Error('Signup is temporarily unavailable. Please try again.'); throw error; }
  finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
}
